import React, { useState } from 'react';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { ContestTiersAwardsHelpDiagram } from './ContestTiersAwardsHelpDiagram';
import { editorialHeadlineSx, midnight } from './midnightStyles';

interface ContestTiersAwardsHelpButtonProps {
    /** Visual size of the help icon button */
    size?: 'small' | 'medium';
    /** Optional aria label override */
    ariaLabel?: string;
}

function HelpSection({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <Box sx={{ mb: 2.5 }}>
            <Typography
                sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: midnight.primary,
                    mb: 1,
                }}>
                {title}
            </Typography>
            <Typography sx={{ fontSize: 14, color: midnight.onSurfaceVariant, lineHeight: 1.6 }}>{children}</Typography>
        </Box>
    );
}

export function ContestTiersAwardsHelpButton({
    size = 'small',
    ariaLabel = 'How tiers and awards work',
}: ContestTiersAwardsHelpButtonProps) {
    const [open, setOpen] = useState(false);

    return (
        <>
            <IconButton
                size={size}
                onClick={() => setOpen(true)}
                aria-label={ariaLabel}
                sx={{
                    color: midnight.onSurfaceVariant,
                    p: size === 'small' ? 0.5 : 1,
                    '&:hover': { color: midnight.primary, bgcolor: 'rgba(255,201,101,0.08)' },
                }}>
                <HelpOutlineIcon sx={{ fontSize: size === 'small' ? 18 : 22 }} />
            </IconButton>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        bgcolor: midnight.surfaceLow,
                        backgroundImage: 'none',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: '16px',
                    },
                }}>
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 2,
                        pb: 1,
                    }}>
                    <Box>
                        <Typography sx={{ ...editorialHeadlineSx, fontSize: 22, color: midnight.onSurface }}>
                            Tiers & awards
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: midnight.onSurfaceVariant, mt: 0.5 }}>
                            How submission tiers and prize payouts work together
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={() => setOpen(false)}
                        aria-label="Close help"
                        sx={{ color: midnight.onSurfaceVariant, mt: -0.5 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 0, pb: 3 }}>
                    <ContestTiersAwardsHelpDiagram />
                    <HelpSection title="Submission tiers">
                        Tiers are the entry levels creators choose when they submit — for example Gold ($99) or Silver
                        ($49). Each tier can have its own entry fee and optional slot limit. Creators pay the tier fee
                        to enter; you fund the prize pool separately before publishing.
                    </HelpSection>
                    <HelpSection title="Award placements">
                        Placements are how you rank winners — such as 1st Place, 2nd Place, or a tier champion. For each
                        placement you define cash prizes (and optional perks like certificates or trophies).
                    </HelpSection>
                    <HelpSection title="Prizes per tier">
                        Every placement pays out per submission tier. If you have Gold and Silver tiers, 1st Place might
                        award $600 for a Gold winner and $100 for a Silver winner. The same creator only wins one payout
                        for their tier and placement.
                    </HelpSection>
                    <HelpSection title="Funding the prize pool">
                        When you publish, the platform totals all cash prizes you configured and reserves that amount
                        from your campaign wallet. Make sure your wallet balance covers the full prize pool before going
                        live.
                    </HelpSection>
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: '10px',
                            bgcolor: midnight.surfaceContainer,
                            border: '1px solid rgba(255,201,101,0.2)',
                        }}>
                        <Typography sx={{ fontSize: 13, color: midnight.onSurface, lineHeight: 1.55 }}>
                            <strong>Example:</strong> Gold ($99 entry) and Silver ($49 entry). 1st Place pays $600 for
                            Gold and $100 for Silver. A creator who enters at Silver and wins 1st Place receives $100 —
                            not the Gold amount.
                        </Typography>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
}
